import { validateManifest } from './types/manifest';
import type { Manifest } from './types/manifest';
import { HttpSource } from './viewer/AssetSource';
import { mountViewer } from './viewer/mountViewer';
import { renderLanding } from './ui/Landing';
import { initTheme, mountThemeToggle } from './ui/theme';
import { installSliderBubble } from './ui/sliderBubble';
import { installTouchGuards } from './ui/touchGuards';
import { topChip, topbarLeft } from './ui/topbar';
import { apiFetch, apiManifestUrl } from './net/origin';
import { accountsOn, type SharedState } from './net/account';
import { isProjectId } from './net/ids';
import { registerServiceWorker } from './ui/serviceWorker';
import { followPanelOpacity } from './ui/appearance';
import { takeAccountLink } from './ui/account/links';

/**
 * App entry. `?tl=<id>` opens the viewer for that project; with no id we show
 * the landing gallery, or Sculpt, Armature or the passkey check when asked,
 * and with accounts on the Account page (`?account`), My projects (`?me`),
 * the Community (`?community`), a member's page in it (`?u=<handle>`) and,
 * for the owner and moderators, the Review page (`?review`) and the viewer
 * in review mode (`?tl=<id>&review=1`).
 * The sign-in links (`?signin`, `?invite=`, `?link=`) open the
 * sign-in dialog over the gallery. Projects load from the API
 * (`/api/projects/:id`); the bundled static demo still works via a
 * fallback so it never depends on the db.
 */
async function main(): Promise<void> {
  initTheme();
  followPanelOpacity();
  mountThemeToggle();
  installSliderBubble();
  installTouchGuards();
  registerServiceWorker();
  const app = document.getElementById('app');
  if (!app) throw new Error('#app element not found');

  const params = new URLSearchParams(window.location.search);
  const id = params.get('tl');
  if (!id && params.get('sculpt') === '1') {
    await bootSculpt();
    return;
  }
  if (!id && params.get('armature') === '1') {
    await bootArmature();
    return;
  }
  // The passkey check (Preferences > Diagnostics, docs/accounts.md batch
  // 0): a page of its own, loaded only when asked for.
  if (!id && params.has('passkeycheck')) {
    const { renderPasskeyCheck } = await import('./ui/passkeyCheck');
    renderPasskeyCheck(app);
    return;
  }
  // The account's pages (docs/accounts.md §7), loaded only when asked for.
  if (!id && params.has('account')) {
    const { renderAccount } = await import('./ui/account/account');
    await renderAccount(app);
    return;
  }
  if (!id && params.has('me')) {
    const { renderMyProjects } = await import('./ui/account/myProjects');
    await renderMyProjects(app);
    return;
  }
  // The community gallery and a member's part of it (docs/accounts.md §13),
  // anyone's to see.
  if (!id && params.has('community')) {
    const { renderCommunity } = await import('./ui/community/page');
    await renderCommunity(app);
    return;
  }
  // The reviewers' page (docs/accounts.md §13): anyone else is sent to the
  // gallery, which says why.
  if (!id && params.has('review')) {
    const { renderReview } = await import('./ui/community/review');
    await renderReview(app);
    return;
  }
  if (!id && params.has('u')) {
    const { renderCommunity } = await import('./ui/community/page');
    await renderCommunity(app, params.get('u') ?? '');
    return;
  }
  if (!id) {
    // A sign-in link is taken off the address first, so nothing else sees
    // it, then answered over the gallery once it has drawn.
    const link = takeAccountLink();
    await renderLanding(app);
    if (link) {
      const { openAccountLink } = await import('./ui/account/signIn');
      const me = await openAccountLink(link).catch(() => null);
      if (me) await renderLanding(app);
    }
    return;
  }
  // The id goes into request paths: `?tl=../media/x/f.json%23` reached the
  // bundled-demo fallback as a path to any JSON on the site. Not an id
  // (net/ids), not a project.
  if (!isProjectId(id)) {
    showError(document.getElementById('overlay'), new Error('That is not a project link'));
    return;
  }
  await bootViewer(id, params.get('review') === '1');
}

/**
 * The bundled demo timelapse ships in the dev server and the test build
 * only (vite.config.ts drops it everywhere else), so only those look for
 * it: elsewhere the fallback could never find a project, only answer for
 * whatever JSON a path happened to reach.
 */
const BUNDLED_DEMO = import.meta.env.DEV || import.meta.env.MODE === 'test';

/**
 * Project-less sculpt entry (/?sculpt=1): boot the viewer on a synthetic
 * one-frame manifest (no API, no timelapse, no transport bar) and mount
 * sculpt mode over it.
 */
async function bootSculpt(): Promise<void> {
  const viewport = document.getElementById('viewport');
  const overlay = document.getElementById('overlay');
  if (!viewport) throw new Error('#viewport element not found');
  const setStatus = (msg: string): void => {
    const box = overlay?.querySelector<HTMLElement>('.overlay__msg');
    if (box) box.textContent = msg;
  };

  // The logo animation plays over the boot, so the wait is the branding;
  // it can only ever ADD time (2.5s, tap to skip), never wedge the boot.
  const { mountSculptSplash } = await import('./ui/SculptSplash');
  const splash = mountSculptSplash(overlay);
  try {
    setStatus('Entering sculpt mode…');
    const { sculptStandaloneProject } = await import('./sculpt/standalone');
    const { manifest, source } = sculptStandaloneProject();
    const viewer = await mountViewer(viewport, manifest, source, setStatus);
    addGalleryLink();
    const { mountSculptMode } = await import('./sculpt/mode');
    await mountSculptMode(viewer);
    // Only now: the synthetic manifest's frame is a placeholder cube, and
    // dropping the overlay before sculpt mode swapped in the live subject
    // showed it as an ugly splash (owner report) - for however long the
    // module import, the autosave read and the subdivision build take. It
    // also left showError printing into a removed overlay if the mount
    // threw. The first visible frame is the real scene - after the logo
    // has finished writing itself, faded rather than yanked.
    await splash.finished;
    if (overlay) {
      overlay.classList.add('overlay--done');
      window.setTimeout(() => overlay.remove(), 450);
    }
  } catch (err) {
    console.error(err);
    splash.dispose(); // the error text needs the plain overlay back
    showError(overlay, err);
  }
}

async function bootViewer(id: string, review = false): Promise<void> {
  const viewport = document.getElementById('viewport');
  const overlay = document.getElementById('overlay');
  if (!viewport) throw new Error('#viewport element not found');

  const base = import.meta.env.BASE_URL; // "/" in production
  const setStatus = (msg: string): void => {
    const box = overlay?.querySelector<HTMLElement>('.overlay__msg');
    if (box) box.textContent = msg;
  };

  try {
    // Review mode (docs/accounts.md §13): a reviewer reads a shared project
    // in any state through the reviewers' route. Anyone else, or a project
    // not shared, goes the usual way, where unlisted work is not found.
    const reviewing = review ? await reviewManifest(id) : null;
    const { manifest, manifestUrl, author } = reviewing ?? (await loadProject(id, base));
    setStatus(manifest.mode === 'model' ? 'Loading model…' : 'Loading timelapse…');
    const viewer = await mountViewer(viewport, manifest, new HttpSource(manifestUrl), setStatus, { author });
    overlay?.remove();
    if (reviewing) {
      const { mountReviewBar } = await import('./ui/community/reviewViewer');
      await mountReviewBar(id, { title: manifest.title, author: author ?? null, shared: reviewing.shared });
      return;
    }
    addGalleryLink();
    // A member's work from the Community (docs/accounts.md §13): who made
    // it, and Report for a member signed in who is not its author.
    if (author) {
      const { mountCommunityCredit } = await import('./ui/community/credit');
      await mountCommunityCredit(id, author);
    }
    // WS0 spike entry (dev-only): ?sculpt=1 mounts sculpt mode on the default
    // sphere over this project's stage/lighting. The real entry point ships
    // with WS5 (see docs/sculpt-mode-implementation.md). Never over someone
    // else's shared work, which is view only.
    if (!author && new URLSearchParams(window.location.search).get('sculpt') === '1') {
      const { mountSculptMode } = await import('./sculpt/mode');
      await mountSculptMode(viewer);
    }
  } catch (err) {
    console.error(err);
    showError(overlay, err);
  }
}

/**
 * Load a project's manifest. Tries the API first, and falls back to a bundled
 * static timelapse (e.g. `?tl=demo`) when there is no API answering. Frame
 * paths resolve against `manifestUrl`, so API manifests (absolute
 * `/media/...`, or full addresses on a files host) and static ones
 * (relative) all work.
 *
 * "No API answering" is not only a 404. A host that serves the app with an
 * SPA fallback answers an unknown /api/ path with 200 and index.html, and
 * parsing that as JSON fails with "Unexpected token '<'" - which is both
 * useless to read and, worse, thrown before the static fallback is ever
 * reached. An HTML answer means no API, so it falls through like a 404
 * does. A JSON answer that will not parse is a real fault and still throws.
 */
async function loadProject(
  id: string,
  base: string,
): Promise<{ manifest: Manifest; manifestUrl: string; author?: string }> {
  const apiPath = `/api/projects/${encodeURIComponent(id)}`;
  const res = await apiFetch(apiPath);
  const servedHtml = res.contentType.includes('text/html');
  if (res.ok && !servedHtml && res.bytes) {
    const raw = JSON.parse(new TextDecoder().decode(res.bytes)) as unknown;
    const manifest = viewerManifest(raw);
    // Frame paths in an API manifest are root-absolute (/media/...), so they
    // resolve against the site, which apiManifestUrl says.
    return { manifest, manifestUrl: apiManifestUrl(apiPath), author: communityAuthor(raw) };
  }
  // A 404 means nothing is answering for this id: try the bundled copy.
  if (!res.ok && res.status !== 404) {
    throw new Error(`Failed to load project (${res.status})`);
  }
  // A private project is a 404 publicly. With accounts on, the account
  // whose project it is reads it through its own routes (My projects,
  // docs/accounts.md §7), whose manifest points the frames at its private
  // media route. The owner, signed in, reads the rest through the
  // Access-gated route, whose manifest points the frames at the gated
  // media route as well.
  const mine = await accountManifest(id);
  if (mine) return mine;
  const owned = await ownerManifest(id);
  if (owned) return owned;

  if (!BUNDLED_DEMO) throw new Error(`Project "${id}" not found`);
  const staticUrl = new URL(`${base}timelapses/${encodeURIComponent(id)}/manifest.json`, window.location.href).href;
  const sres = await fetch(staticUrl);
  if (!sres.ok) throw new Error(`Project "${id}" not found`);
  return { manifest: validateManifest(await sres.json()), manifestUrl: staticUrl };
}

/**
 * With accounts on, the signed-in account's own project's manifest (GET
 * /api/me/projects/:id), or null: anyone else's, signed out, or accounts
 * off, so the owner's route and the bundled fallback still get their turn.
 */
async function accountManifest(id: string): Promise<{ manifest: Manifest; manifestUrl: string } | null> {
  if (!(await accountsOn().catch(() => false))) return null;
  const path = `/api/me/projects/${encodeURIComponent(id)}`;
  try {
    const res = await apiFetch(path);
    if (!res.ok || !res.bytes || !res.contentType.includes('application/json')) return null;
    const manifest = viewerManifest(JSON.parse(new TextDecoder().decode(res.bytes)));
    return { manifest, manifestUrl: apiManifestUrl(path) };
  } catch (err) {
    if (err instanceof SceneProjectError) throw err;
    return null;
  }
}

/**
 * The owner's manifest for a project the public API does not show, or null.
 * For anyone else Access answers with its login page (a redirect, which
 * apiFetch reports as signed out rather than following, or HTML), all of
 * which read as "not here" so the bundled fallback still gets its turn.
 */
async function ownerManifest(id: string): Promise<{ manifest: Manifest; manifestUrl: string } | null> {
  const path = `/admin/api/projects/${encodeURIComponent(id)}`;
  try {
    const res = await apiFetch(path);
    if (!res.ok || !res.bytes || !res.contentType.includes('application/json')) return null;
    const manifest = viewerManifest(JSON.parse(new TextDecoder().decode(res.bytes)));
    return { manifest, manifestUrl: apiManifestUrl(path) };
  } catch (err) {
    if (err instanceof SceneProjectError) throw err;
    return null;
  }
}

/**
 * A shared project's manifest for review (GET /api/mod/projects/:id), its
 * files on the reviewers' private route, with its author and where it
 * stands; null for anyone not a reviewer (401, 403), a project not shared
 * (404), accounts off, or no answer, so the usual path takes over.
 */
async function reviewManifest(
  id: string,
): Promise<{ manifest: Manifest; manifestUrl: string; author?: string; shared: SharedState } | null> {
  if (!(await accountsOn().catch(() => false))) return null;
  const path = `/api/mod/projects/${encodeURIComponent(id)}`;
  try {
    const res = await apiFetch(path);
    if (!res.ok || !res.bytes || !res.contentType.includes('application/json')) return null;
    const raw = JSON.parse(new TextDecoder().decode(res.bytes)) as { shared?: unknown; author?: { handle?: unknown } | null };
    const manifest = viewerManifest(raw);
    const handle = typeof raw.author?.handle === 'string' && raw.author.handle ? raw.author.handle : undefined;
    const shared = typeof raw.shared === 'string' ? (raw.shared as SharedState) : 'pending';
    return { manifest, manifestUrl: apiManifestUrl(path), author: handle, shared };
  } catch (err) {
    if (err instanceof SceneProjectError) throw err;
    return null;
  }
}

/**
 * The author's handle when the public answer is a member's work from the
 * Community (`community: true, author: {handle}`, docs/accounts.md §13);
 * undefined for a template, or anything else.
 */
function communityAuthor(raw: unknown): string | undefined {
  const r = raw as { community?: unknown; author?: { handle?: unknown } } | null;
  if (r?.community !== true || typeof r.author?.handle !== 'string' || !r.author.handle) return undefined;
  return r.author.handle;
}

class SceneProjectError extends Error {}

/**
 * A manifest the viewer can play. A scene project has a file and no frames:
 * it opens in Sculpt, and saying so beats "frames must be a non-empty array".
 */
function viewerManifest(raw: unknown): Manifest {
  const r = raw as { mode?: string; title?: string };
  if (r?.mode === 'scene') {
    throw new SceneProjectError(`"${r.title ?? 'This'}" is a scene saved from Sculpt: open it from the gallery`);
  }
  if (r?.mode === 'armature') {
    throw new SceneProjectError(`"${r.title ?? 'This'}" is an armature: it opens in Armature mode, from the gallery or My projects`);
  }
  return validateManifest(raw);
}

/**
 * Armature entry (/?armature=1): the same viewer boot as sculpt, with the
 * armature mode mounted over it instead.
 */
async function bootArmature(): Promise<void> {
  const viewport = document.getElementById('viewport');
  const overlay = document.getElementById('overlay');
  if (!viewport) throw new Error('#viewport element not found');
  const setStatus = (msg: string): void => {
    const box = overlay?.querySelector<HTMLElement>('.overlay__msg');
    if (box) box.textContent = msg;
  };
  const { mountSculptSplash } = await import('./ui/SculptSplash');
  const splash = mountSculptSplash(overlay);
  try {
    setStatus('Entering armature mode…');
    const { sculptStandaloneProject } = await import('./sculpt/standalone');
    const { manifest, source } = sculptStandaloneProject();
    const viewer = await mountViewer(viewport, manifest, source, setStatus);
    addGalleryLink();
    const { mountArmatureMode } = await import('./armature/mode');
    await mountArmatureMode(viewer);
    await splash.finished;
    if (overlay) {
      overlay.classList.add('overlay--done');
      window.setTimeout(() => overlay.remove(), 450);
    }
  } catch (err) {
    console.error(err);
    splash.dispose();
    showError(overlay, err);
  }
}

function addGalleryLink(): void {
  // The gallery is the site's root, whatever the address says: a path such
  // as `//elsewhere.example/` reaches this page through the host's own
  // fallback for unknown paths (the worker's answered it too, before it
  // took a list), and used as a link it is another site.
  const a = topChip('← Gallery', '/');
  a.classList.add('viewer-back');
  topbarLeft().appendChild(a);
}

function showError(overlay: HTMLElement | null, err: unknown): void {
  const message = err instanceof Error ? err.message : String(err);
  if (overlay) {
    overlay.classList.add('overlay--error');
    overlay.innerHTML = '';
    const box = document.createElement('div');
    box.className = 'overlay__msg';
    box.textContent = `Could not load project: ${message}`;
    overlay.appendChild(box);
  }
}

void main();
